'use client';

/* eslint-disable @next/next/no-img-element -- Transparent cutouts keep their own framing; next/image would crop or resample them. */

import { useEffect, useRef, useState } from 'react';

const ASSETS = '/roulette-concepts';
export const CLOSED_BOX = `${ASSETS}/packaging-v3/closed-box.webp`;
const PLACEHOLDER = '/placeholder.png';

// Every cutout was cut from exactly this catalog photo. When the admin uploads a new
// photo (or puts a new roll into the pool), the live catalog photo is shown instead.
const CUTOUT_SOURCES: Record<number, string> = {
  100128:
    'https://cdn.aziatochka.ru/aziatochka/products/products/holodskreventkoi-1768225176641-441899379.webp',
  100131:
    'https://cdn.aziatochka.ru/aziatochka/products/products/25_1788292905155_678600822_1788294016197_239215151.webp',
  100132: 'https://cdn.aziatochka.ru/aziatochka/products/_30/krevet-1768227497610-383425628.webp',
  100133: 'https://cdn.aziatochka.ru/aziatochka/products/products/7996_1777408476509_22667116.webp',
  100134: 'https://cdn.aziatochka.ru/aziatochka/products/products/7205_1777408791113_364375192.webp',
  100135: 'https://cdn.aziatochka.ru/aziatochka/products/products/1_1788293953544_262664767.webp',
  100171: 'https://cdn.aziatochka.ru/aziatochka/products/products/8_1788292669194_671758388.webp',
  100172: 'https://cdn.aziatochka.ru/aziatochka/products/products/21_1788292843200_795165675.webp',
};

export type PrizeArt = {
  /** Whole open-box photograph on the same canvas as the closed box, when one was shot. */
  openBox: string | null;
  /** Transparent cutout, else the square catalog photo, else the placeholder. */
  image: string;
  /** Current catalog photo, used if a prepared asset cannot be downloaded. */
  catalogImage: string;
  cutout: boolean;
};

export function prizeArt(product: { id: number; image_url?: string | null }): PrizeArt {
  const source = CUTOUT_SOURCES[product.id];
  const prepared = Boolean(source) && source === product.image_url;
  return {
    openBox: prepared ? `${ASSETS}/packaging-v3/open-box-${product.id}.webp` : null,
    image: prepared ? `${ASSETS}/cutouts/${product.id}.webp` : product.image_url || PLACEHOLDER,
    catalogImage: product.image_url || PLACEHOLDER,
    cutout: prepared,
  };
}

/** A missing file must not leave a broken image in the reveal or the pool list. */
type PrizeImageProps = {
  art: PrizeArt;
  className?: string;
  loading?: 'lazy' | 'eager';
  onReady?: () => void;
};

export function PrizeImage(props: PrizeImageProps) {
  // Reset failed sources when the server changes either the product or its photo.
  return <ResolvedPrizeImage key={`${props.art.image}\n${props.art.catalogImage}`} {...props} />;
}

function ResolvedPrizeImage({
  art,
  className,
  loading,
  onReady,
}: PrizeImageProps) {
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const candidates = [...new Set([art.image, art.catalogImage, PLACEHOLDER])];
  const source = candidates.find((candidate) => !failedSources.includes(candidate));
  // Even an unavailable placeholder must not produce a browser's broken-image glyph.
  if (!source) return <span className={className} data-cutout="false" aria-hidden="true" />;
  const failSource = () => {
    if (!active.current) return;
    setFailedSources((failed) => failed.includes(source) ? failed : [...failed, source]);
    if (source === candidates.at(-1)) onReady?.();
  };
  return (
    <img
      className={className}
      data-cutout={art.cutout && source === art.image}
      src={source}
      alt=""
      draggable={false}
      decoding="async"
      loading={loading}
      onLoad={(event) => {
        const image = event.currentTarget;
        const decoded = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
        void decoded.then(() => {
          if (active.current) onReady?.();
        }, failSource);
      }}
      onError={failSource}
    />
  );
}
